import { orderBy } from 'lodash-es';

import {
  OutputConfiguration,
  PerformanceValue,
  StudySubcategory,
  StudySubcategoryItem,
  StudyUnit,
} from '@mitsubishi/shared/data-access';
import { MiscUtils } from '@mitsubishi/utils';

import { isNil } from '@angular-ru/cdk/utils';

export class StudyUtils {
  static getSubcategoryUnits(subcategories: StudySubcategory[]): Record<string, StudyUnit> {
    // Use Object.assign for better performance than spread operator with large objects
    const allUnits: Record<string, StudyUnit> = {};
    for (const subcategory of subcategories) {
      Object.assign(allUnits, subcategory.units);
    }
    return allUnits;
  }

  static getFilteredPerformanceValues(performanceValues: PerformanceValue[], outputs: OutputConfiguration[]) {
    const performances: PerformanceValue[] = [];
    for (const output of outputs) {
      const performance = performanceValues.find(perfValue =>
        output.circuitId === null
          ? perfValue.id === output.id
          : perfValue.id === output.id && perfValue.circuit === output.circuitId
      );
      if (performance && !this.hidePerformanceValue(output, performance)) {
        performances.push({
          ...performance,
          circuit: output.circuitId,
          resourceId: output.resourceId,
          prefixResourceId: output.prefixResourceId,
          suffixResourceId: output.suffixResourceId,
          extendedResourceId: output.extendedResourceId,
          iconResourceId: output.iconResourceId,
          isVisibleByDefault: output.isVisibleByDefault,
        });
      }
    }

    return performances;
  }

  static hidePerformanceValue(output: OutputConfiguration, performanceValue: PerformanceValue) {
    if (output.visibilityOutputId == null) {
      return false;
    }

    return output.visibilitySymbol === '=='
      ? performanceValue.realValue === output.visibilityValue
      : output.visibilitySymbol === '!='
        ? performanceValue.realValue !== output.visibilityValue
        : false;
  }

  static getSubcategoriesUnitPerformanceItems(data: {
    subcategories: StudySubcategory[];
    subcategoryId: string | undefined;
    outputs: OutputConfiguration[];
  }): StudySubcategoryItem[] {
    const { subcategories, subcategoryId, outputs } = data;

    const allUnits = this.getSubcategoryUnits(subcategories);
    const filteredSubcategories = isNil(subcategoryId)
      ? subcategories
      : subcategories.filter(subcategory => subcategory.uuid === subcategoryId);

    const items = filteredSubcategories.reduce((acc, subcategory) => {
      const validUnits = Object.values(subcategory.units).filter(unit => !unit.isExternalUnit);
      if (validUnits.length === 0) return acc;

      // Pre-process working conditions for better performance
      const workingConditions = orderBy(Object.values(subcategory.workingConditions), ['createdAt'], ['asc']);
      const wcIndexMap = new Map<string, number>();
      const wcCreatedAtMap = new Map<string, number>();

      workingConditions.forEach((wc, index) => {
        wcIndexMap.set(wc.uuid, index);
        wcCreatedAtMap.set(wc.uuid, new Date(wc.createdAt).getTime());
      });

      for (const unit of validUnits) {
        const unitPerformancesKeys = MiscUtils.getObjectKeys(unit.performances);

        // Sort performances by working condition createdAt (optimized with pre-computed timestamps)
        const sortedPerformancesKeys = unitPerformancesKeys.sort((a, b) => {
          const wcTimestampA = wcCreatedAtMap.get(unit.performances[a].workingConditionId) || 0;
          const wcTimestampB = wcCreatedAtMap.get(unit.performances[b].workingConditionId) || 0;

          // First sort by working condition timestamp
          if (wcTimestampA !== wcTimestampB) {
            return wcTimestampA - wcTimestampB;
          }

          return 0;
        });

        for (let i = 0; i < sortedPerformancesKeys.length; i++) {
          const performanceId = sortedPerformancesKeys[i];
          const unitPerformance = unit.performances[performanceId];
          const workingCondition = subcategory.workingConditions[unitPerformance.workingConditionId];

          if (!workingCondition) continue;

          const unitWcData = unit.workingConditionsData[workingCondition.uuid];
          const unitPerformanceData = unitPerformance.performance;

          // Pre-compute values to avoid repeated operations
          const wcIndex = wcIndexMap.get(unitPerformance.workingConditionId) || 0;
          const prevWorkingConditionId =
            i > 0 ? unit.performances[sortedPerformancesKeys[i - 1]].workingConditionId : '';

          // Pre-convert objects to arrays for better performance
          const messages = unitPerformanceData?.messages ? Object.values(unitPerformanceData.messages) : [];
          const envelopes = unitPerformanceData?.envelopes ? Object.values(unitPerformanceData.envelopes) : [];
          const accessories = unitPerformanceData?.accessories ? Object.values(unitPerformanceData.accessories) : [];
          const performanceValuesArray = unitPerformanceData?.performanceValues
            ? Object.values(unitPerformanceData.performanceValues)
            : [];

          acc.push({
            unit,
            performanceId,
            subcategory,
            performanceIndex: unitPerformance.index ?? 0,
            workingConditionCreatedAt: new Date(workingCondition.createdAt).getTime(),
            workingCondition,
            rowSpan: 1,
            isMainWorkingCondition: wcIndex === 0,
            prevWorkingConditionId,
            externalUnit: allUnits[unitWcData?.externalUnitId],
            notes: unitPerformance.note ?? '',
            isGoodForSelection: unitPerformance.goodForSelection ?? false,
            performance: {
              hasEnvelopes: unitPerformanceData?.hasEnvelopes,
              erpCompliant: unitPerformanceData?.erpCompliant,
              messages,
              envelopes,
              accessories,
              performanceValues: unitPerformance?.hasPerformanceAccessories
                ? []
                : this.getFilteredPerformanceValues(performanceValuesArray, outputs),
            },
          });
        }
      }

      return acc;
    }, [] as StudySubcategoryItem[]);

    // Sort items by model size and model name to ensure consistent initial ordering
    // This groups units together and orders them alphabetically
    const sortedItems = items.sort((a, b) => {
      // First, compare by modelSize
      const modelSizeA = a.unit.modelSize ?? '';
      const modelSizeB = b.unit.modelSize ?? '';
      const modelSizeComparison = modelSizeA.localeCompare(modelSizeB);
      if (modelSizeComparison !== 0) {
        return modelSizeComparison;
      }

      // If modelSize is the same, compare by modelName
      return a.unit.modelName.localeCompare(b.unit.modelName);
    });

    // Calculate initial rowspans: first row in each unit group gets the full count, others get 1
    this.calculateRowSpansForItems(sortedItems);

    return sortedItems;
  }

  /**
   * Calculates rowSpans for consecutive items with the same unit UUID.
   * First row in each group gets the full rowspan (= group size), others get 1.
   */
  static calculateRowSpansForItems(items: StudySubcategoryItem[]): void {
    if (!items || items.length === 0) return;

    let currentUnitId = '';
    let currentGroupStart = 0;

    for (let i = 0; i <= items.length; i++) {
      const item = items[i];
      const unitId = item?.unit.uuid;
      const isEndOfArray = i === items.length;
      const isNewGroup = unitId !== currentUnitId || isEndOfArray;

      if (isNewGroup && currentGroupStart < i) {
        // Finalize the current group
        const groupSize = i - currentGroupStart;

        // Set rowspan: first row gets full count, others get 1
        for (let j = currentGroupStart; j < i; j++) {
          items[j].rowSpan = j === currentGroupStart ? groupSize : 1;
        }

        // Start new group (unless we're at the end)
        if (!isEndOfArray) {
          currentUnitId = unitId;
          currentGroupStart = i;
        }
      } else if (!isEndOfArray && currentUnitId === '') {
        // Initialize first group
        currentUnitId = unitId;
        currentGroupStart = i;
      }
    }
  }
}
